import React, { useState, useEffect } from "react";
import { Box, Tooltip } from "@mui/material";

interface InteractiveGaugeProps {
  id: string;
  percent: number;
  label: React.ReactNode;
  amount: string;
  initialSelectedBlocks?: number[];
  onSelectedBlocksChange?: (selectedBlocks: number[]) => void;
  readonly?: boolean;
  maxBlocks?: number; // מעל המגבלה — ניתן לסמן אבל נחשב לא תקין
}

const InteractiveGauge: React.FC<InteractiveGaugeProps> = ({
  id,
  percent,
  label,
  amount,
  initialSelectedBlocks = [],
  onSelectedBlocksChange,
  readonly = false,
  maxBlocks,
}) => {
  // Track which blocks are selected (10 blocks, each representing 10%)
  const [selectedBlocks, setSelectedBlocks] = useState<Set<number>>(
    new Set(initialSelectedBlocks)
  );
  const [hoveredBlock, setHoveredBlock] = useState<number | null>(null);

  // עדכון מ-props אם משתנה — מושווה לפי תוכן ולא reference
  const initialKey = initialSelectedBlocks.slice().sort().join(",");
  useEffect(() => {
    setSelectedBlocks(new Set(initialSelectedBlocks));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  const totalBlocks = 10; // 10 blocks for 0-100%

  const handleBlockClick = (blockIndex: number) => {
    const newSelected = new Set(selectedBlocks);
    if (newSelected.has(blockIndex)) {
      newSelected.delete(blockIndex);
    } else {
      newSelected.add(blockIndex);
    }
    setSelectedBlocks(newSelected);

    if (onSelectedBlocksChange) {
      onSelectedBlocksChange(Array.from(newSelected).sort());
    }
  };

  const isOverLimit = maxBlocks !== undefined && selectedBlocks.size > maxBlocks;

  // Calculate needle position based on percent
  const needleAngle = -90 + percent * 180; // -90 to 90 degrees

  // Calculate text color based on needle position and selected blocks
  const getTextColor = () => {
    if (selectedBlocks.size === 0) {
      return "#000"; // Default black if no blocks selected
    }

    // מעל 100% — נחשב כבלוק 9 (90%-100%)
    const needleBlockIndex = percent >= 1.0 ? 9 : Math.floor(percent * 10);
    const selectedBlocksArray = Array.from(selectedBlocks);

    // Check if needle is in a selected (green) block
    if (selectedBlocks.has(needleBlockIndex)) {
      return "#2E7D32"; // Green
    }

    // Find minimum distance to any selected block
    const minDistance = Math.min(
      ...selectedBlocksArray.map(blockIndex => Math.abs(blockIndex - needleBlockIndex))
    );

    // Distance is in blocks (each block is 10%)
    const distancePercent = minDistance * 10;

    if (distancePercent <= 20) {
      return "#FF9800"; // Orange - within 20%
    } else {
      return "#D32F2F"; // Red - more than 20%
    }
  };

  return (
    <Box textAlign="center" position="relative">
      {/* SVG Gauge with blocks */}
      <Box position="relative" display="inline-block">
        <svg width="300" height="200" viewBox="0 0 300 180">
          <defs>
            {/* Define gradient for green fill */}
            <linearGradient id={`greenGradient-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: "#66BB6A", stopOpacity: 0.8 }} />
              <stop offset="100%" style={{ stopColor: "#4CAF50", stopOpacity: 0.9 }} />
            </linearGradient>
          </defs>

          {/* Center point */}
          <g transform="translate(165, 95)">
            {/* Draw 10 arc blocks */}
            {Array.from({ length: totalBlocks }).map((_, index) => {
              const startAngle = -90 + (index * 180) / totalBlocks;
              const endAngle = -90 + ((index + 1) * 180) / totalBlocks;
              const innerRadius = 75;
              const outerRadius = 92.5; // Reduced thickness by half

              const startAngleRad = (startAngle * Math.PI) / 180;
              const endAngleRad = (endAngle * Math.PI) / 180;

              // Calculate path points
              const x1 = innerRadius * Math.cos(startAngleRad);
              const y1 = innerRadius * Math.sin(startAngleRad);
              const x2 = outerRadius * Math.cos(startAngleRad);
              const y2 = outerRadius * Math.sin(startAngleRad);
              const x3 = outerRadius * Math.cos(endAngleRad);
              const y3 = outerRadius * Math.sin(endAngleRad);
              const x4 = innerRadius * Math.cos(endAngleRad);
              const y4 = innerRadius * Math.sin(endAngleRad);

              const isSelected = selectedBlocks.has(index);
              const isHovered = hoveredBlock === index;

              const rangeLabel = `${index * 10}-${(index + 1) * 10}%`;

              return (
                <Tooltip key={index} title={rangeLabel} arrow placement="right" enterDelay={0} enterNextDelay={0}>
                  <path
                    d={`
                      M ${x1} ${y1}
                      L ${x2} ${y2}
                      A ${outerRadius} ${outerRadius} 0 0 1 ${x3} ${y3}
                      L ${x4} ${y4}
                      A ${innerRadius} ${innerRadius} 0 0 0 ${x1} ${y1}
                      Z
                    `}
                    fill={isSelected ? `url(#greenGradient-${id})` : "transparent"}
                    stroke={isHovered ? "#2E7D32" : "#999"}
                    strokeWidth={isHovered ? 2.5 : 1.5}
                    style={{
                      cursor: readonly ? "default" : "pointer",
                      transition: "all 0.2s ease",
                      filter: isSelected ? "drop-shadow(0 0 4px rgba(76, 175, 80, 0.6))" : "none",
                    }}
                    onMouseEnter={() => setHoveredBlock(index)}
                    onMouseLeave={() => setHoveredBlock(null)}
                    onClick={() => !readonly && handleBlockClick(index)}
                  />
                </Tooltip>
              );
            })}

            {/* Needle */}
            <line
              x1="0"
              y1="0"
              x2={Math.cos((needleAngle * Math.PI) / 180) * 65}
              y2={Math.sin((needleAngle * Math.PI) / 180) * 65}
              stroke="#444"
              strokeWidth="3"
              strokeLinecap="round"
              style={{ pointerEvents: "none" }}
            />

            {/* Needle base */}
            <circle cx="0" cy="0" r="8" fill="#888" style={{ pointerEvents: "none" }} />
          </g>

          {/* Percentage text */}
          <text
            x="155"
            y="125"
            textAnchor="middle"
            fontSize="21"
            fontWeight="bold"
            fill={getTextColor()}
            style={{ pointerEvents: "none" }}
          >
            {(percent * 100).toFixed(1)}%
          </text>
        </svg>
      </Box>

      {/* Label and amount below */}
      <Box mt={1} display="flex" alignItems="center" justifyContent="center" gap={0.5}>
        <strong>{label}</strong>
        {selectedBlocks.size > 0 && getTextColor() === "#2E7D32" && !isOverLimit && (
          <span style={{ color: "#2E7D32", fontSize: "18px", fontWeight: "bold" }}>✓</span>
        )}
      </Box>
      <Box>{amount}</Box>
      {isOverLimit && (
        <Box sx={{ color: "#D32F2F", fontSize: "0.78rem", fontWeight: 600, mt: 0.5 }}>
          טווח חורג מהמותר ({maxBlocks! * 10}%)
        </Box>
      )}
    </Box>
  );
};

export default InteractiveGauge;
